/**
 * BI — Bloqueios descritivo — modal de filtros, prévia e CSV.
 */
import api from '../api.js';
import { translate } from '../../versa/filter.js';
import msgbox, { notify } from '../../versa/msgbox.js';
import dialog from '../../versa/dialog.js';
import progress, { downloadResponseWithProgress } from '../../versa/progress.js';
import { formatBiHttpError } from './bi-http-error.js';

function $(sel, root = document) {
	return root.querySelector(sel);
}

const PREVIEW_COLS = [
	['empresa', 'EMPRESA'],
	['sku', 'SKU'],
	['produto', 'PRODUTO'],
	['armazem', 'ARMAZÉM'],
	['posicao', 'POSIÇÃO'],
	['qtd_disponivel', 'QTD DISPONÍVEL'],
	['qtd_reservada', 'QTD RESERVADA'],
	['saidas_que_reservaram', 'SAÍDAS QUE RESERVARAM'],
	['qtd_bloqueada', 'QTD BLOQUEADA'],
	['motivo_bloqueio', 'MOTIVO BLOQUEIO'],
];

function skuLabel(p) {
	if (!p?.sku) return '';
	return Array.isArray(p.sku) ? p.sku.join('') : String(p.sku);
}

function filterExprCompany(companyId) {
	return translate({
		'field-0': 'company_id',
		'operator-0': 'eq',
		'value-0': companyId,
	});
}

async function loadCompanies(selectEl) {
	const res = await api.list_all_companies();
	if (!res.ok) {
		notify(await res.text(), 'Empresas');
		return;
	}
	let list;
	try {
		list = await res.json();
	} catch (e) {
		notify('Resposta de empresas inválida.', 'Empresas');
		return;
	}
	if (!Array.isArray(list)) return;
	selectEl.innerHTML = '<option value="">— Selecione —</option>';
	for (const c of list) {
		const o = document.createElement('option');
		o.value = c.id;
		o.textContent = c.nick || c.name || c.id;
		selectEl.appendChild(o);
	}
}

async function loadWarehouses(companyId, sel) {
	sel.innerHTML = '<option value="">Todos</option>';
	if (!companyId) return;
	const fe = encodeURIComponent(filterExprCompany(companyId));
	const res = await api.list_warehouses(`?filter=${fe}&limit=0&sort=2`);
	if (!res.ok) {
		notify(await res.text(), 'Armazéns');
		return;
	}
	const arr = await res.json();
	if (!Array.isArray(arr)) return;
	for (const w of arr) {
		const o = document.createElement('option');
		o.value = String(w.id);
		o.textContent = `${w.name ?? ''} (${w.id})`;
		sel.appendChild(o);
	}
}

async function loadPrograms(companyId, sel) {
	sel.innerHTML = '<option value="">Todos</option>';
	if (!companyId) return;
	const res = await api.list_programs_by_company(companyId);
	if (!res.ok) {
		notify(await res.text(), 'Programas');
		return;
	}
	const arr = await res.json();
	if (!Array.isArray(arr)) return;
	for (const p of arr) {
		const o = document.createElement('option');
		o.value = String(p.id);
		o.textContent = p.name || String(p.id);
		sel.appendChild(o);
	}
}

async function loadProductsForCompany(companyId, selectEl, searchInput) {
	selectEl.innerHTML = '';
	if (!companyId) {
		selectEl.innerHTML = '<option value="">— Escolha a empresa —</option>';
		return;
	}
	selectEl.innerHTML = '<option value="">Carregando produtos…</option>';
	selectEl.disabled = true;
	const mb = msgbox({ animation: true, text: 'Carregando produtos…' });
	try {
		const fe = filterExprCompany(companyId);
		const batch = 500;
		const products = [];
		const seen = new Set();
		for (let offset = 0; ; offset += batch) {
			const q = `?filter=${encodeURIComponent(fe)}&limit=${batch}&offset=${offset}&sort=7&include_disabled=1`;
			const res = await api.list_products(q);
			if (!res.ok) {
				notify(await res.text(), 'Produtos');
				selectEl.innerHTML = '<option value="">— Erro —</option>';
				return;
			}
			const chunk = await res.json();
			if (!Array.isArray(chunk) || chunk.length === 0) break;
			for (const p of chunk) {
				const id = p?.id;
				if (id == null || seen.has(id)) continue;
				seen.add(id);
				products.push(p);
			}
			if (chunk.length < batch) break;
		}
		selectEl.innerHTML = '<option value="">— Todos (ou filtre SKU/nome) —</option>';
		for (const p of products) {
			const sku = skuLabel(p);
			const name = (p.name || '').trim() || '(sem nome)';
			const o = document.createElement('option');
			o.value = String(p.id);
			const dis = p.is_disabled ? ' [inativo]' : '';
			o.textContent = `${sku} — ${name} (id ${p.id})${dis}`;
			selectEl.appendChild(o);
		}
		if (searchInput) applyProductSearch(selectEl, searchInput.value);
	} finally {
		selectEl.disabled = false;
		mb.destroy();
	}
}

function applyProductSearch(selectEl, needle) {
	const n = (needle || '').trim().toLowerCase();
	if (!n) {
		for (const o of selectEl.options) o.hidden = false;
		return;
	}
	for (const o of selectEl.options) {
		if (!o.value) {
			o.hidden = false;
			continue;
		}
		o.hidden = !String(o.textContent || '').toLowerCase().includes(n);
	}
}

function collectParams(node, format) {
	const companyId = $('#bsbd-company', node)?.value?.trim();
	if (!companyId) return null;
	const sp = new URLSearchParams();
	sp.set('format', format);
	const wh = $('#bsbd-warehouse', node)?.value?.trim();
	if (wh) sp.set('warehouse_id', wh);
	const prog = $('#bsbd-program', node)?.value?.trim();
	if (prog) sp.set('program_id', prog);
	const prod = $('#bsbd-product', node)?.value?.trim();
	if (prod) sp.set('product_id', prod);
	else {
		const ps = $('#bsbd-product-search', node)?.value?.trim();
		if (ps) sp.set('product_search', ps);
	}
	const lot = $('#bsbd-lot', node)?.value?.trim();
	if (lot) sp.set('lot_search', lot);
	const mode = $('#bsbd-mode', node)?.value || 'blocked';
	sp.set('mode', mode);
	if (format === 'json') {
		sp.set('limit', $('#bsbd-limit', node)?.value || '200');
		sp.set('offset', $('#bsbd-offset', node)?.value || '0');
	} else {
		const mx = $('#bsbd-max-rows', node)?.value?.trim();
		if (mx) sp.set('max_rows', mx);
	}
	return sp;
}

function escCell(s) {
	if (s == null) return '';
	const t = document.createElement('template');
	t.textContent = String(s);
	return t.innerHTML;
}

function renderPreviewTable(container, rows) {
	if (!rows?.length) {
		container.innerHTML = '<p class="muted mts">Nenhuma linha (ajuste filtros ou permissões).</p>';
		return;
	}
	const keys = PREVIEW_COLS.map((c) => c[0]);
	const labels = PREVIEW_COLS.map((c) => c[1]);
	let html = '<div class="bsbd-scroll mts"><table class="view fsx"><thead><tr>';
	for (const lbl of labels) {
		html += `<th>${escCell(lbl)}</th>`;
	}
	html += '</tr></thead><tbody>';
	for (const r of rows) {
		html += '<tr>';
		for (const k of keys) {
			html += `<td>${escCell(r[k])}</td>`;
		}
		html += '</tr>';
	}
	html += '</tbody></table></div>';
	container.innerHTML = html;
}

function downloadCsv(blob, filename) {
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename || 'bloqueios-descritivo.csv';
	a.click();
	URL.revokeObjectURL(a.href);
}

export default async function bi_stock_blocks_descriptive() {
	const work = document.getElementById('workarea');
	work.innerHTML = `
		<style>
			.bsbd-hint { color: #555; font-size: 0.9rem; max-width: 48rem; }
			.bsbd-scroll { overflow: auto; max-height: 65vh; }
			.bsbd-modal label { display: block; margin-top: 0.65rem; }
			.bsbd-modal .row { display: flex; flex-wrap: wrap; gap: 0.75rem 1rem; align-items: flex-end; margin-top: 0.35rem; }
			.bsbd-modal .row > label { flex: 1 1 14rem; }
			.bsbd-product-select { width: 100%; min-height: 11rem; margin-top: 0.35rem; font-size: 0.88rem; }
			.bsbd-actions { margin-top: 1rem; display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; }
		</style>
		<div class="title">Bloqueios descritivo (BI)</div>
		<p class="mts bsbd-hint">Foco do relatório: posições com estoque <strong>BLOQUEADO</strong>. Mostra quantidades, último motivo de bloqueio e (opcional) saídas com reserva ATIVA na célula. Linhas 100% zeradas nunca aparecem. Exporte <strong>CSV</strong> ou prévia na tela.</p>
		<p class="mts"><button type="button" class="button" id="bsbd-open-modal">Filtros e geração</button></p>
		<div id="bsbd-preview-host" class="mts"></div>
	`;

	const previewHost = $('#bsbd-preview-host', work);

	const openModal = () => {
		const dlgHtml = `
<div class="msgbox bsbd-modal" style="min-width: min(40rem, 92vw); max-width: 96vw; text-align: left;">
	<div class="mb-title">Bloqueios descritivo — filtros</div>
	<div class="mb-text" style="max-height: none;">
		<label>Empresa<sup>*</sup><select id="bsbd-company"></select></label>
		<div class="row">
			<label>Programa<select id="bsbd-program"></select></label>
			<label>Armazém<select id="bsbd-warehouse"></select></label>
		</div>
		<label>Filtrar lista de produtos (SKU ou nome)<input type="search" id="bsbd-prod-filter" placeholder="opcional" autocomplete="off"></label>
		<label>Produto<select id="bsbd-product" class="bsbd-product-select" size="10"></select></label>
		<p class="muted fsxx" style="margin:0.25rem 0 0;">Deixe produto em «Todos» e use o campo abaixo para buscar no servidor, ou escolha um item na lista.</p>
		<label>Busca produto (servidor)<input type="text" id="bsbd-product-search" placeholder="trecho SKU ou nome" autocomplete="off"></label>
		<label>Lote (contém)<input type="text" id="bsbd-lot" placeholder="opcional" autocomplete="off"></label>
		<label>Modo
			<select id="bsbd-mode">
				<option value="blocked" selected>Apenas BLOQUEADOS (qtd bloqueada &gt; 0)</option>
				<option value="blocked_or_reserved">Bloqueados + Reservados</option>
				<option value="all">Todas as linhas com saldo (linhas 100% zeradas continuam ocultas)</option>
			</select>
		</label>
		<div class="row">
			<label>Prévia: limite<input type="number" id="bsbd-limit" value="200" min="1" max="5000" step="1"></label>
			<label>Prévia: offset<input type="number" id="bsbd-offset" value="0" min="0" step="1"></label>
			<label>CSV: máx. linhas<input type="number" id="bsbd-max-rows" value="50000" min="1" max="200000" step="1"></label>
		</div>
	</div>
	<div class="bsbd-actions">
		<button type="button" class="button bn_Preview">Prévia na tela</button>
		<button type="button" class="button bn_ExportCsv">Exportar CSV</button>
		<button type="button" class="button bn_Escape">Fechar</button>
	</div>
</div>`;

		const dlg = dialog.create(dlgHtml, async (key) => {
			const root = dlg.getNode();
			if (key === 'Escape') {
				dlg.destroy();
				return;
			}
			if (key !== 'Preview' && key !== 'ExportCsv') return;

			const sp = collectParams(root, key === 'Preview' ? 'json' : 'csv');
			if (!sp) {
				notify('Selecione a empresa.');
				return;
			}
			const companyId = $('#bsbd-company', root).value.trim();

			if (key === 'Preview') {
				const mb = msgbox({ animation: true, text: 'Buscando linhas para prévia…' });
				try {
					const res = await api.stock_blocks_descriptive_report(companyId, sp.toString());
					const raw = await res.text();
					if (!res.ok) {
						notify(formatBiHttpError(res.status, raw) || res.statusText, 'Bloqueios descritivo');
						return;
					}
					let data;
					try {
						data = JSON.parse(raw);
					} catch (e) {
						notify('Resposta não é JSON.', 'Bloqueios descritivo');
						return;
					}
					if (!Array.isArray(data)) {
						notify('Formato inesperado da API.', 'Bloqueios descritivo');
						return;
					}
					dlg.destroy();
					renderPreviewTable(previewHost, data);
					notify(`Prévia: ${data.length} linha(s).`, 'Bloqueios descritivo');
				} catch (e) {
					console.error(e);
					notify(e?.message || String(e), 'Bloqueios descritivo');
				} finally {
					mb.destroy();
				}
				return;
			}

			const pg = progress({
				title: 'Gerando CSV de bloqueios…',
				text: 'O servidor está consolidando estoque, reservas e motivos de bloqueio. A barra fica indeterminada enquanto a query roda e passa a 0→100% real assim que o download começa.',
				indeterminate: true,
			});
			try {
				pg.markStage('Aguardando resposta do servidor (query SQL)…');
				const res = await api.stock_blocks_descriptive_report(companyId, sp.toString());
				if (!res.ok) {
					const t = await res.text();
					notify(formatBiHttpError(res.status, t), 'Bloqueios descritivo');
					return;
				}
				pg.markStage('Recebendo CSV…');
				const blob = await downloadResponseWithProgress(res, pg);
				const cd = res.headers.get('Content-Disposition');
				let fname = 'bloqueios-descritivo.csv';
				if (cd) {
					const m = cd.match(/filename="?([^";]+)"?/i);
					if (m) fname = m[1];
				}
				downloadCsv(blob, fname);
				dlg.destroy();
				notify('Download do CSV iniciado.', 'Bloqueios descritivo');
			} catch (e) {
				console.error(e);
				notify(e?.message || String(e), 'Bloqueios descritivo');
			} finally {
				pg.destroy();
			}
		});

		const root = dlg.getNode();
		const selCo = $('#bsbd-company', root);
		const selWh = $('#bsbd-warehouse', root);
		const selPr = $('#bsbd-program', root);
		const selProd = $('#bsbd-product', root);
		const inpFilt = $('#bsbd-prod-filter', root);

		loadCompanies(selCo).then(() => {});
		selCo.addEventListener('change', async () => {
			const id = selCo.value;
			await Promise.all([
				loadWarehouses(id, selWh),
				loadPrograms(id, selPr),
				loadProductsForCompany(id, selProd, inpFilt),
			]);
		});
		inpFilt.addEventListener('input', () => applyProductSearch(selProd, inpFilt.value));
		selProd.addEventListener('change', () => applyProductSearch(selProd, inpFilt.value));
	};

	$('#bsbd-open-modal', work).addEventListener('click', openModal);
}
